import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { saveWebsite, updateWebsite } from '../../redux/slices/websiteSlice';
import logger from '../../utils/logger';
import { get } from '../../services/apiClient';
import { PREFERRED_ICON_TYPE, TypeConverters } from '../../config/TypeConfig';
import { colorOptions, getRandomColor } from '../../utils/colorUtils';
import './WebsitePopup.css';
import AppIcon from '../AppIcon';
import trackingManager from '../../services/trackingManager';
import languageManager from '../../utils/languageManager';

// 网站添加/编辑弹窗组件
const WebsitePopup = ({ onClose, onSave, onSaveAndContinue, initialData, isInContainer = false }) => {
  // 判断是否为编辑模式
  const isEditMode = !!initialData;

  // 添加调试日志
  useEffect(() => {
    if (isEditMode) {
      logger.log('[弹窗-编辑模式] 初始化数据:', {
        preferredIconType: initialData?.preferredIconType,
        ico: initialData?.ico,
        hasValidIco: !!initialData?.ico,
        userHasSelectedIconType
      });
    }
  }, []);

  const [websiteUrl, setWebsiteUrl] = useState(initialData?.url || '');
  const [websiteName, setWebsiteName] = useState(initialData?.name || '');
  const [websiteDesc, setWebsiteDesc] = useState(initialData?.description || '');
  const [textIcon, setTextIcon] = useState(initialData?.textIcon || '');
  const [iconUrl, setIconUrl] = useState('');
  const [bgColor, setBgColor] = useState(initialData?.bgColor || '#1E90FF');
  const [isLoading, setIsLoading] = useState(false);
  const [urlError, setUrlError] = useState('');
  const [ico, setIco] = useState(initialData?.ico || ''); // ico状态
  const [hasValidIco, setHasValidIco] = useState(!!initialData?.ico); // 是否有有效的ico图标
  const [selectedIconType, setSelectedIconType] = useState(
    TypeConverters.convertIconType(initialData?.preferredIconType, initialData)
  ); // 用户选择的图标类型：根据数据智能推断
  const popupRef = useRef(null);

  // 添加一个状态来跟踪用户是否手动选择过图标类型
  // 在编辑模式下，如果有 preferredIconType，说明用户之前已经选择过了
  const [userHasSelectedIconType, setUserHasSelectedIconType] = useState(
    isEditMode && initialData?.preferredIconType !== undefined
  );

  const isResettingRef = useRef(false);
  /** 递增请求序号，用于丢弃过期的 fetch 结果 */
  const websiteFetchRequestIdRef = useRef(0);
  /** URL 输入防抖：延迟后再请求站点信息 */
  const urlFetchDebounceRef = useRef(null);
  /** 上次发起抓取的主机名（小写），主机变化时允许再次自动填充标题/描述 */
  const lastFetchHostKeyRef = useRef(null);
  const userEditedWebsiteNameRef = useRef(false);
  const userEditedWebsiteDescRef = useRef(false);

  // 生成唯一ID
  const generateUniqueId = (url) => {
    const timestamp = new Date().getTime();
    const randomStr = Math.random().toString(36).substring(2, 8);
    const urlHash = url.split('//').pop().split('/')[0].replace(/\./g, '_');
    return `${urlHash}_${timestamp}_${randomStr}`;
  };

  // 网站信息缓存
  const websiteInfoCache = {};
  
  // 使用API获取网站title和图标
  const fetchWebsiteTitleFromAPI = async (url, options = { timeout: 5000 }) => {
    try {
      // 检查缓存
      const cacheKey = url.toLowerCase();
      if (websiteInfoCache[cacheKey]) {
        logger.log('使用缓存的网站信息');
        return websiteInfoCache[cacheKey];
      }

      logger.log('开始获取网站信息:', url);
      
      // 构建API URL - 使用新的API接口
      const apiUrl = `/proxy/website-info?url=${encodeURIComponent(url)}`;
      
      // 使用apiClient发送请求
      const response = await get(apiUrl);
      
      // 构建返回结果
      const result = {
        title: response.title || null,
        description: response.description || null,
        iconUrl: response.ico || null
      };
      
      // 存入缓存
      websiteInfoCache[cacheKey] = result;
      logger.log('成功获取网站信息:', result);
      
      return result;
    } catch (error) {
      logger.error('获取网站信息失败:', error.message);
      try {
        // 如果API调用失败，尝试使用备用图标URL
        const urlObj = new URL(url);
        return {
          title: null,
          description: null,
          iconUrl: null
        };
      } catch (urlError) {
        logger.error('URL解析失败:', urlError.message);
        return {
          title: null,
          description: null,
          iconUrl: null
        };
      }
    }
  };

  // 获取网站信息
  const fetchWebsiteInfo = async (url) => {
    if (url == null || String(url).trim() === '') {
      return;
    }

    let processedUrl = String(url).trim();
    if (!processedUrl.startsWith('http://') && !processedUrl.startsWith('https://')) {
      processedUrl = 'https://' + processedUrl;
    }

    let hostKey = '';
    try {
      hostKey = new URL(processedUrl).hostname.toLowerCase();
    } catch {
      hostKey = processedUrl.replace(/^https?:\/\//, '').split('/')[0].toLowerCase();
    }
    if (hostKey && hostKey !== lastFetchHostKeyRef.current) {
      userEditedWebsiteNameRef.current = false;
      userEditedWebsiteDescRef.current = false;
      lastFetchHostKeyRef.current = hostKey;
    }

    const requestId = ++websiteFetchRequestIdRef.current;
    setIsLoading(true);
    setUrlError('');

    try {
      // 提取域名
      let domain = processedUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
      domain = domain.split('/')[0];

      // 获取favicon地址
      const defaultIcoUrl = `https://${domain}/favicon.ico`;

      // 提取网站名称 - 只使用域名主体部分，不添加AI后缀
      const domainParts = domain.split('.');
      let siteName = '';
      if (domainParts.length >= 2) {
        siteName = domainParts[0];
        if (siteName === 'www' && domainParts.length > 2) {
          siteName = domainParts[1];
        }
        siteName = siteName.charAt(0).toUpperCase() + siteName.slice(1);
      } else {
        siteName = domain;
      }

      const randomColor = getRandomColor();
      setBgColor(randomColor);

      let extractedIcoUrl = null;
      const defaultDesc = languageManager.t('websitePopup.officialWebsite', { domain });
      let computedDesc = defaultDesc;

      try {
        const { title, description, iconUrl } = await fetchWebsiteTitleFromAPI(processedUrl, { timeout: 3000 });
        if (requestId !== websiteFetchRequestIdRef.current) {
          return;
        }
        if (title) {
          siteName = title;
        }
        if (iconUrl) {
          extractedIcoUrl = iconUrl;
        }
        computedDesc = description || defaultDesc;
      } catch (titleError) {
        logger.error('Error fetching website info:', titleError);
        if (requestId !== websiteFetchRequestIdRef.current) {
          return;
        }
        computedDesc = defaultDesc;
      }

      if (requestId !== websiteFetchRequestIdRef.current) {
        return;
      }

      const extractedTextIcon = siteName.substring(0, 4);
      if (!userEditedWebsiteNameRef.current) {
        setWebsiteName(siteName);
      }
      if (!userEditedWebsiteDescRef.current) {
        setWebsiteDesc(computedDesc);
      }
      setTextIcon(extractedTextIcon);

      const checkIconLoads = (iconUrl) => {
        return new Promise((resolve) => {
          if (iconUrl && iconUrl.startsWith('data:image/')) {
            logger.log('使用base64编码图标，无需验证加载');
            resolve(true);
            return;
          }

          const img = new Image();
          img.onload = () => {
            if (img.width > 0 && img.height > 0) {
              resolve(true);
            } else {
              resolve(false);
            }
          };
          img.onerror = () => resolve(false);
          img.src = iconUrl;

          setTimeout(() => resolve(false), 500);
        });
      };

      const [extractedIcoLoads, defaultIcoLoads] = await Promise.all([
        extractedIcoUrl ? checkIconLoads(extractedIcoUrl) : Promise.resolve(false),
        checkIconLoads(defaultIcoUrl)
      ]);

      if (requestId !== websiteFetchRequestIdRef.current) {
        return;
      }

      if (extractedIcoLoads) {
        setIconUrl(extractedIcoUrl);
        setIco(extractedIcoUrl);
        setHasValidIco(true);
        setSelectedIconType(PREFERRED_ICON_TYPE.ICO);
        setBgColor('white');
        logger.log('成功获取到图标，设置白色背景');
      } else if (defaultIcoLoads) {
        setIconUrl(defaultIcoUrl);
        setIco(defaultIcoUrl);
        setHasValidIco(true);
        setSelectedIconType(PREFERRED_ICON_TYPE.ICO);
        setBgColor('white');
        logger.log('成功获取到默认图标，设置白色背景');
      } else {
        setIconUrl('');
        setIco('');
        setHasValidIco(false);
        setSelectedIconType(PREFERRED_ICON_TYPE.TEXT);
        logger.log('未获取到图标，使用随机背景色:', randomColor);
      }

      setUrlError('');
    } catch (error) {
      logger.error('Error fetching website info:', error);
      if (requestId !== websiteFetchRequestIdRef.current) {
        return;
      }
      setUrlError(languageManager.t('websitePopup.fetchFailed'));

      const urlForFallback = String(url).trim();
      if (urlForFallback) {
        try {
          const domainParts = urlForFallback.replace(/^https?:\/\//, '').split('.');
          let defaultName = domainParts[0];
          if (defaultName === 'www' && domainParts.length > 2) {
            defaultName = domainParts[1];
          }
          setTextIcon(defaultName.substring(0, 4).toUpperCase());
          setHasValidIco(false);
          setSelectedIconType(PREFERRED_ICON_TYPE.TEXT);

          const randomColorErr = getRandomColor();
          logger.log('错误情况下设置的随机颜色:', randomColorErr);
          setBgColor(randomColorErr);
        } catch (e) {
          setTextIcon('网站');
          const randomColorErr = getRandomColor();
          logger.log('严重错误情况下设置的随机颜色:', randomColorErr);
          setBgColor(randomColorErr);
        }
      } else {
        setTextIcon('网站');
        const randomColorErr = getRandomColor();
        logger.log('无URL情况下设置的随机颜色:', randomColorErr);
        setBgColor(randomColorErr);
      }
    } finally {
      if (requestId === websiteFetchRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  // 添加点击外部关闭弹窗的处理 - 只在不在容器中时启用
  useEffect(() => {
    // 如果在容器中，不监听点击外部关闭，由容器组件处理
    if (isInContainer) {
      return;
    }
    
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, isInContainer]);

  useEffect(() => {
    return () => {
      if (urlFetchDebounceRef.current) {
        clearTimeout(urlFetchDebounceRef.current);
        urlFetchDebounceRef.current = null;
      }
    };
  }, []);

  // 处理URL输入变化和自动获取（防抖，减少并发与乱序）
  const handleUrlChange = (e) => {
    const value = e.target.value;
    setWebsiteUrl(value);

    if (urlFetchDebounceRef.current) {
      clearTimeout(urlFetchDebounceRef.current);
      urlFetchDebounceRef.current = null;
    }

    if (value && value.length > 5) {
      isResettingRef.current = false;
      const urlRegex = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(\/\S*)?$/i;
      if (urlRegex.test(value)) {
        urlFetchDebounceRef.current = setTimeout(() => {
          urlFetchDebounceRef.current = null;
          fetchWebsiteInfo(value);
        }, 400);
      }
    }
  };

  // 处理URL输入框失去焦点：取消待执行的防抖并立即拉取
  const handleUrlBlur = (e) => {
    const raw = (e?.target?.value ?? '').trim();
    setTimeout(() => {
      if (isResettingRef.current) return;
      if (urlFetchDebounceRef.current) {
        clearTimeout(urlFetchDebounceRef.current);
        urlFetchDebounceRef.current = null;
      }
      if (raw) {
        const urlRegex = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(\/\S*)?$/i;
        if (urlRegex.test(raw)) {
          fetchWebsiteInfo(raw);
        } else {
          setUrlError(languageManager.t('websitePopup.enterValidUrl'));
        }
      }
    }, 0);
  };

  // 只在初始加载或者 ico 发生变化且用户没有手动选择过时才自动设置图标类型
  useEffect(() => {
    // 如果用户已经手动选择过图标类型，就不再自动切换
    if (userHasSelectedIconType) {
      return;
    }
    
    // 在编辑模式下，如果有保存的 preferredIconType，应该使用保存的值
    if (isEditMode && initialData?.preferredIconType !== undefined) {
      // 编辑模式下已经在初始化时设置了 selectedIconType，不需要再次设置
      return;
    }
    
    // 只在初始化时根据是否有 ico 来设置默认图标类型（仅适用于新建模式）
    if (ico && ico.trim() && hasValidIco) {
      setSelectedIconType(PREFERRED_ICON_TYPE.ICO);
      logger.log('[弹窗] 检测到有效图标，默认选择ICO模式');
    } else if (!ico || !ico.trim()) {
      setSelectedIconType(PREFERRED_ICON_TYPE.TEXT);
      logger.log('[弹窗] 没有图标，默认选择文字图标模式');
    }
  }, [ico, hasValidIco, userHasSelectedIconType, isEditMode, initialData?.preferredIconType]);

  // 修改 handleSelectIconType 函数，标记用户已手动选择
  const handleSelectIconType = (type) => {
    if (type === PREFERRED_ICON_TYPE.ICO && !hasValidIco) return;
    setSelectedIconType(type);
    setUserHasSelectedIconType(true); // 标记用户已手动选择
  };

  // 处理表单提交
  const handleSave = (shouldClose = true, skipOnSave = false) => {
    if (!websiteName.trim()) {
      alert(languageManager.t('websitePopup.enterWebsiteName'));
      return;
    }
    
    // 确保URL格式正确
    let processedUrl = websiteUrl;
    if (!processedUrl.startsWith('http://') && !processedUrl.startsWith('https://')) {
      processedUrl = 'https://' + processedUrl;
    }
    
    // 最终背景颜色 - 确保使用当前选择的颜色
    const finalBgColor = bgColor;
    
    // 确保 selectedIconType 有有效值
    const finalIconType = selectedIconType || PREFERRED_ICON_TYPE.TEXT;
    
    logger.log("保存数据 - selectedIconType:", finalIconType, "类型:", typeof finalIconType);
    
    const savedData = {
      id: initialData?.id || generateUniqueId(processedUrl),
      name: websiteName,
      url: processedUrl,
      description: websiteDesc,
      iconUrl: null, // 不使用iconUrl，统一用ico
      textIcon: textIcon,
      iconColor: '#ffffff',
      bgColor: finalBgColor, // 使用当前状态中的背景颜色
      ico: ico || '', // 如果ico有值就使用，没有就设为空字符串
      preferredIconType: finalIconType // 使用确保有效的图标类型
    };
    
    // 如果是编辑模式，保留complexId和其他可能需要的字段
    if (isEditMode && initialData) {
      if (initialData.complexId) {
        savedData.complexId = initialData.complexId;
      }
      // 保留其他可能需要的系统字段
      if (initialData.createdAt) {
        savedData.createdAt = initialData.createdAt;
      }
      if (initialData.updatedAt) {
        savedData.updatedAt = new Date().toISOString();
      }
    }
    
    logger.log('[WebsitePopup] 最终保存的数据:', savedData);
    
    // 上报网站管理埋点
    if (isEditMode) {
      trackingManager.reportWebsiteAction('EDIT', websiteName);
    } else {
      trackingManager.reportWebsiteAction('ADD', websiteName);
    }
    
    if (!skipOnSave) {
      onSave(savedData);
    }
    if (shouldClose) {
      onClose();
    }
    return savedData;
  };
  
  // 处理背景颜色变化
  const handleColorChange = (color) => {
    logger.log("用户选择了颜色:", color);
    setBgColor(color);
  };

  // 清空表单方法
  const resetForm = () => {
    isResettingRef.current = true;
    websiteFetchRequestIdRef.current += 1;
    if (urlFetchDebounceRef.current) {
      clearTimeout(urlFetchDebounceRef.current);
      urlFetchDebounceRef.current = null;
    }
    lastFetchHostKeyRef.current = null;
    userEditedWebsiteNameRef.current = false;
    userEditedWebsiteDescRef.current = false;
    setWebsiteUrl('');
    setWebsiteName('');
    setWebsiteDesc('');
    setTextIcon('');
    setIconUrl('');
    setBgColor('#1E90FF');
    setIco('');
    setHasValidIco(false);
    setSelectedIconType(PREFERRED_ICON_TYPE.TEXT);
    setIsLoading(false);
    setUrlError('');
    setUserHasSelectedIconType(false);
  };

  const popupContent = (
    <div className="popup-overlay">
      <div className="app-popup" ref={popupRef}>
        {/* 关闭按钮 - 只在非容器模式或编辑模式时显示 */}
        {(!isInContainer || isEditMode) && (
          <button className="edit-popup-close-button" onClick={onClose}>
            ×
          </button>
        )}
        
        <div className="app-popup-content">
          {isEditMode && (
            <h3 style={{color: 'white', fontSize: '18px', textAlign: 'center', marginTop: 0, marginBottom: '20px'}}>
              {languageManager.t('websitePopup.editWebsiteNavigation')}
            </h3>
          )}
          
          {/* 网络地址 */}
          <div className="app-popup-field">
            <label>
              {languageManager.t('websitePopup.websiteUrl')}
              <span style={{ 
                color: '#ff4d4f', 
                marginLeft: '4px', 
                fontSize: '14px',
                fontWeight: 'bold'
              }}>*</span>
            </label>
            <div className="url-input-container">
              {isLoading && <div className="loading-indicator" style={{color: 'white'}}>{languageManager.t('websitePopup.fetching')}</div>}
              <input
                type="text"
                className="app-popup-input"
                placeholder="https://www.aidengta.cn"
                value={websiteUrl}
                onChange={handleUrlChange}
                onBlur={handleUrlBlur}
              />
              <div className="char-count">{websiteUrl.length} / 1000</div>
              {urlError && <div style={{color: '#ff5252', fontSize: '12px', marginTop: '4px'}}>{urlError}</div>}
            </div>
          </div>
          
          {/* 链接名称 */}
          <div className="app-popup-field">
            <label>
              {languageManager.t('websitePopup.websiteName')}
              <span style={{ 
                color: '#ff4d4f', 
                marginLeft: '4px', 
                fontSize: '14px',
                fontWeight: 'bold'
              }}>*</span>
            </label>
            <div className="input-with-count">
              <input
                type="text"
                className="app-popup-input"
                placeholder={languageManager.t('websitePopup.websiteNamePlaceholder')}
                value={websiteName}
                onChange={(e) => {
                  userEditedWebsiteNameRef.current = true;
                  setWebsiteName(e.target.value);
                }}
                maxLength={100}
              />
              <div className="char-count">{websiteName.length} / 100</div>
            </div>
          </div>
          
          {/* 网址简介 */}
          <div className="app-popup-field">
            <label>{languageManager.t('websitePopup.websiteDesc')}</label>
            <div className="input-with-count">
              <input
                type="text"
                className="app-popup-input"
                placeholder={languageManager.t('websitePopup.websiteDescPlaceholder')}
                value={websiteDesc}
                onChange={(e) => {
                  userEditedWebsiteDescRef.current = true;
                  setWebsiteDesc(e.target.value);
                }}
                maxLength={200}
              />
              <div className="char-count">{websiteDesc.length} / 200</div>
            </div>
          </div>
          
          {/* 文字图标 */}
          <div className="app-popup-field">
            <label>{languageManager.t('websitePopup.textIcon')}</label>
            <div className="input-with-count">
              <input
                type="text"
                className="app-popup-input"
                placeholder={languageManager.t('websitePopup.textIconPlaceholder')}
                value={textIcon}
                onChange={(e) => {
                  const value = e.target.value;
                  // 如果是输入法正在输入（composition）的状态，允许临时超出限制
                  if (e.nativeEvent.isComposing) {
                    setTextIcon(value);
                    return;
                  }
                  // 输入法完成输入后，截取前4个字符
                  const chars = Array.from(value);
                  setTextIcon(chars.slice(0, 4).join(''));
                }}
                onCompositionEnd={(e) => {
                  // 输入法输入完成时，截取前4个字符
                  const value = e.target.value;
                  const chars = Array.from(value);
                  setTextIcon(chars.slice(0, 4).join(''));
                }}
              />
              <div className="char-count">{Array.from(textIcon).length} / 4</div>
            </div>
          </div>
          
          {/* 图标选择 - 只在有有效ico图标时显示选择界面 */}
          {hasValidIco ? (
          <div className="app-popup-field">
            <label>{languageManager.t('websitePopup.selectIcon')}</label>
              <div className="icon-selection" style={{display: 'flex', marginBottom: '15px'}}>
                {/* 文字图标选项 */}
              <div 
                  className="icon-option"
                  onClick={() => handleSelectIconType(PREFERRED_ICON_TYPE.TEXT)}
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '12px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: 'transparent',
                    margin: '0 15px 0 0',
                  cursor: 'pointer',
                    border: selectedIconType === PREFERRED_ICON_TYPE.TEXT ? '3px solid #4d91ea' : '1px solid #e0e0e0',
                  position: 'relative',
                  overflow: 'hidden',
                  boxSizing: 'border-box'
                }}
                >
                <AppIcon 
                  textIcon={textIcon || languageManager.t('websitePopup.textIcon')}
                  bgColor={bgColor}
                  size={56}
                />
                
                  {selectedIconType === PREFERRED_ICON_TYPE.TEXT && (
                  <div style={{
                    position: 'absolute',
                      top: '2px',
                      right: '2px',
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: '#4d91ea',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '10px',
                    color: 'white',
                    fontWeight: 'bold',
                    zIndex: 2
                  }}>
                    ✓
                  </div>
                )}
              </div>
              
                {/* ico图标选项 */}
                <div 
                  className="icon-option"
                  onClick={() => handleSelectIconType(PREFERRED_ICON_TYPE.ICO)}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: 'transparent',
                    margin: 0,
                    cursor: 'pointer',
                    border: selectedIconType === PREFERRED_ICON_TYPE.ICO ? '3px solid #4d91ea' : '1px solid #e0e0e0',
                    position: 'relative',
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                  }}
                >
                  <AppIcon 
                    ico={ico}
                    bgColor={bgColor}
                    size={56}
                  />
                  
                  {selectedIconType === PREFERRED_ICON_TYPE.ICO && (
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      backgroundColor: '#4d91ea',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: '10px',
                      color: 'white',
                      fontWeight: 'bold',
                      zIndex: 2
                    }}>
                      ✓
                    </div>
                  )}
                </div>
            </div>
          </div>
          ) : (
          <div className="app-popup-field">
              <label>{languageManager.t('websitePopup.iconPreview')}</label>
              <div className="icon-preview" style={{display: 'flex', alignItems: 'center'}}>
                <div 
                  className="text-icon-preview"
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: bgColor === 'transparent' ? 
                      'linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee), linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee)' : 
                      bgColor,
                    backgroundSize: bgColor === 'transparent' ? '16px 16px' : undefined,
                    backgroundPosition: bgColor === 'transparent' ? '0 0, 8px 8px' : undefined,
                    margin: '0 10px 10px 0',
                    position: 'relative',
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                  }}
                >
                  <AppIcon 
                    textIcon={textIcon || '辅'} 
                    bgColor={bgColor}
                    size={56}
                  />
                </div>
                
                <div style={{color: 'white', fontSize: '12px', marginLeft: '10px'}}>
                  {languageManager.t('websitePopup.noIconUseText')}
                </div>
              </div>
            </div>
          )}
          
          {/* 背景颜色 */}
          <div className="app-popup-field">
            <label>{languageManager.t('websitePopup.bgColor')}</label>
            <div className="color-options">
              {colorOptions.map(option => (
                <div
                  key={option.id}
                  className={`color-option ${bgColor === option.color ? 'selected' : ''} ${option.color === 'transparent' ? 'transparent' : ''}`}
                  style={{ 
                    background: option.color,
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onClick={() => handleColorChange(option.color)}
                >
                  {option.displayName && <span style={{
                    fontSize: '10px', 
                    color: 'white', 
                    textShadow: '0 0 2px rgba(0,0,0,0.5)', 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    width: '100%',
                    textAlign: 'center'
                  }}>{option.displayName}</span>}
                </div>
              ))}
            </div>
          </div>
          
          {/* 保存按钮 */}
          <div style={{ display: 'flex', gap: '20px'}}>
            <button 
              className="save-btn primary" 
              onClick={() => handleSave(true)}
            >
              {isEditMode ? languageManager.t('websitePopup.saveChanges') : languageManager.t('websitePopup.saveAndClose')}
            </button>
            {!isEditMode && (
              <button 
                className="save-btn secondary"
                onClick={() => {
                  if (onSaveAndContinue) {
                    // 只保存，不关闭弹窗
                    const savedData = handleSave(false, true); // 新增参数 skipOnSave
                    onSaveAndContinue(savedData);
                  } else {
                    handleSave(false);
                  }
                  resetForm();
                }}
              >
                {languageManager.t('websitePopup.saveAndContinue')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // 如果在容器中，不使用Portal
  if (isInContainer) {
    return popupContent;
  }

  // 否则使用Portal
  return ReactDOM.createPortal(
    popupContent,
    document.getElementById('modal-root')
  );
};

export default WebsitePopup; 